import React, { useState, useEffect } from "react";
import { FiX, FiSlash, FiUnlock } from "react-icons/fi";
import toast from "react-hot-toast";

import { fetchEventBlockLog, fetchEventHeldPoints } from "../../../utils/eventApi";

const getUserId = (row) => row?.user_id ?? row?.userId ?? row?.userID ?? row?.id ?? null;

const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-GB", {
        timeZone: "UTC",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }) + " UTC";
};

/** Held-points breakdown rows worth showing: label + non-zero amount. */
const HELD_LABELS = [
  ["daily_mining", "Mining"],
  ["daily_check_in", "Check-in"],
  ["referral", "Referral"],
  ["streak_bonus", "Streak"],
  ["creator_hub", "Creator Hub"],
  ["mini_game", "Mini Games"],
  ["task", "Tasks"],
  ["other", "Other"],
];

/**
 * Block or unblock one participant from this event, with a reason either way.
 *
 * Blocking only affects this event: the user drops off its leaderboard and sees
 * 0 event points. Points from before the block are kept as they are. Anything
 * earned while blocked is held aside, and the unblock asks the admin whether to
 * add it to the user's event points or keep it out. The user's earlier blocks
 * and unblocks are listed below the form so the admin can see why it happened
 * before.
 */
const BlockUserModal = ({ isOpen, onClose, onSubmit, participant = null, eventId }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Unblock only: what was earned while blocked, and the admin's choice for it.
  const [held, setHeld] = useState(null);
  const [heldLoading, setHeldLoading] = useState(false);
  const [heldAction, setHeldAction] = useState("");

  const rawUserId = getUserId(participant);
  const userIdStr = rawUserId !== undefined && rawUserId !== null ? String(rawUserId) : "";
  const isBlocked = !!participant?.is_blocked;
  const heldTotal = Number(held?.held_points) || 0;

  useEffect(() => {
    setReason("");
    setHeld(null);
    setHeldAction("");
    if (!isOpen || !userIdStr || !eventId) return;

    let cancelled = false;
    setHistoryLoading(true);
    fetchEventBlockLog(eventId, { userId: userIdStr, limit: 20 })
      .then((res) => !cancelled && setHistory(res.data || []))
      .catch(() => !cancelled && setHistory([]))
      .finally(() => !cancelled && setHistoryLoading(false));

    // Fresh, not the list row's: mining keeps adding to it while the dialog opens.
    if (isBlocked) {
      setHeldLoading(true);
      fetchEventHeldPoints(eventId, userIdStr)
        .then((res) => !cancelled && setHeld(res.data || null))
        .catch(() => !cancelled && setHeld(null))
        .finally(() => !cancelled && setHeldLoading(false));
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, userIdStr, eventId, isBlocked]);

  if (!isOpen || !participant) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) {
      toast.error(`Please give a reason for the ${isBlocked ? "unblock" : "block"} (min 3 characters)`);
      return;
    }
    if (!userIdStr) {
      toast.error("Participant user ID is missing");
      return;
    }
    // No default on purpose: adding a suspected cheater's points by accident is
    // the mistake this choice exists to prevent.
    if (isBlocked && heldTotal !== 0 && !heldAction) {
      toast.error("Choose whether to add the points earned while blocked, or keep them out");
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        user_id: userIdStr,
        reason: reason.trim(),
        block: !isBlocked,
        ...(isBlocked && heldAction ? { held_points_action: heldAction } : {}),
      });
      toast.success(
        !isBlocked
          ? "User blocked from this event"
          : heldTotal !== 0
          ? `User unblocked — ${heldTotal.toLocaleString()} held points ${heldAction === "add" ? "added" : "kept out"}`
          : "User unblocked from this event"
      );
      onClose();
    } catch (err) {
      toast.error(err.message || `Failed to ${isBlocked ? "unblock" : "block"} user`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isBlocked ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
              }`}
            >
              {isBlocked ? <FiUnlock size={16} /> : <FiSlash size={16} />}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">
                {isBlocked ? "Unblock Participant" : "Block Participant"}
              </h2>
              <p className="text-xs text-gray-400">Applies to this event only</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
          >
            <FiX size={17} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* User Info Box */}
          <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-800">
                {participant.username || participant.email || "Unknown User"}
              </p>
              <p className="text-[11px] text-gray-400 font-mono truncate max-w-[200px]">{userIdStr}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Event Points</p>
              <p className="text-sm font-bold text-gray-800">
                {Number(participant.total_points || 0).toLocaleString()}
              </p>
            </div>
          </div>

          {/* What will happen */}
          {isBlocked ? (
            <div className="p-3 bg-green-50/60 rounded-xl border border-green-100 text-xs text-green-800 space-y-1">
              <p>Currently blocked: <strong>{participant.block_reason || "—"}</strong></p>
              <p className="text-green-700">
                On unblock the user's points from before the block show again and they return to the
                leaderboard.
              </p>
            </div>
          ) : (
            <div className="p-3 bg-red-50/60 rounded-xl border border-red-100 text-xs text-red-700">
              The user will be removed from this event's leaderboard and will see 0 event points. Points
              from before the block are kept. Anything they earn while blocked is held aside — when you
              unblock, you choose whether to add it.
            </div>
          )}

          {/* Unblock: points earned while blocked */}
          {isBlocked && (
            <div className="p-3.5 rounded-xl border border-amber-200 bg-amber-50/60 space-y-2.5">
              {heldLoading ? (
                <p className="text-xs text-amber-700">Checking points earned while blocked...</p>
              ) : !held ? (
                <p className="text-xs text-amber-700">
                  Could not load the points earned while blocked. If there are any, the unblock will ask
                  again.
                </p>
              ) : heldTotal === 0 ? (
                <p className="text-xs text-amber-800">This user earned no event points while blocked.</p>
              ) : (
                <>
                  <p className="text-xs text-amber-900">
                    While blocked, this user earned{" "}
                    <strong className="text-sm">{heldTotal.toLocaleString()} event points</strong>. They are
                    not counted yet.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {HELD_LABELS.filter(([key]) => Number(held.held_breakdown?.[key]) !== 0 && held.held_breakdown?.[key] != null).map(
                      ([key, label]) => (
                        <span
                          key={key}
                          className="px-2 py-0.5 bg-white border border-amber-200 rounded-lg text-[11px] text-amber-800"
                        >
                          {label}: <strong>{Number(held.held_breakdown[key]).toLocaleString()}</strong>
                        </span>
                      )
                    )}
                  </div>

                  <div className="space-y-1.5 pt-1">
                    {[
                      [
                        "add",
                        `Add ${heldTotal.toLocaleString()} points to their event points`,
                        "They count toward rank and prizes, on the days they were earned.",
                      ],
                      [
                        "discard",
                        "Keep them out",
                        "Not counted. Still recorded for this event in the block history.",
                      ],
                    ].map(([value, label, hint]) => (
                      <label
                        key={value}
                        className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition ${
                          heldAction === value ? "bg-white border-amber-400 ring-2 ring-amber-400/20" : "bg-white/60 border-amber-100"
                        }`}
                      >
                        <input
                          type="radio"
                          name="held_points_action"
                          value={value}
                          checked={heldAction === value}
                          onChange={() => setHeldAction(value)}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="block text-xs font-semibold text-gray-800">{label}</span>
                          <span className="block text-[11px] text-gray-500">{hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              maxLength={512}
              placeholder={
                isBlocked
                  ? "e.g. Review complete, no multi-account activity found"
                  : "e.g. Multiple accounts farming referral points"
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              required
            />
            <p className="text-[11px] text-gray-400 mt-1">
              {isBlocked
                ? "Recorded in the block history"
                : "The user sees this reason in the app while blocked"}
            </p>
          </div>

          {/* Block history */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1.5">Block history</p>
            {historyLoading ? (
              <p className="text-[11px] text-gray-400">Loading...</p>
            ) : history.length === 0 ? (
              <p className="text-[11px] text-gray-400">No earlier blocks for this user in this event.</p>
            ) : (
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {history.map((h) => (
                  <li key={h.id} className="p-2 rounded-lg border border-gray-100 text-[11px]">
                    <div className="flex items-center justify-between">
                      <span
                        className={`px-1.5 py-0.5 rounded font-semibold uppercase ${
                          h.action === "block" ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"
                        }`}
                      >
                        {h.action}
                      </span>
                      <span className="text-gray-400">{formatWhen(h.created_at)}</span>
                    </div>
                    <p className="text-gray-700 mt-1">{h.reason}</p>
                    {h.held_points_action && (
                      <p className={h.held_points_action === "added" ? "text-green-700" : "text-amber-700"}>
                        {Number(h.held_points).toLocaleString()} points earned while blocked —{" "}
                        {h.held_points_action === "added" ? "added" : "kept out"}
                      </p>
                    )}
                    {h.admin_email && <p className="text-gray-400">by {h.admin_email}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className={`px-5 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition flex items-center gap-2 disabled:opacity-60 ${
              isBlocked
                ? "bg-green-600 hover:bg-green-700 shadow-green-600/20"
                : "bg-red-600 hover:bg-red-700 shadow-red-600/20"
            }`}
          >
            {loading && (
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isBlocked ? "Unblock User" : "Block User"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockUserModal;
